/**
 * How a turn's replay is played, step by step, and how long it takes.
 *
 * The client walks the log of a register one step at a time; this says
 * which logged events are steps. The server needs the same count, to add
 * the replay's length to a turn-mode clock that would otherwise run while
 * a player watches the turn they just played. Both read it from here, so
 * the two cannot disagree.
 */
/** Between steps of a register's playback, unless the host set another:
 * live, in a turn-mode turn's replay, and replayed from the log. */
export const PLAYBACK_MS = 500;
export const PLAYBACK_MS_MIN = 50;
export const PLAYBACK_MS_MAX = 3000;
/** A host's step length, held to 50-3000 ms; anything not a number is
 * the standard 500. */
export function playbackMs(ms) {
    const n = Math.round(Number(ms));
    if (ms === undefined || ms === null || !Number.isFinite(n))
        return PLAYBACK_MS;
    return Math.min(PLAYBACK_MS_MAX, Math.max(PLAYBACK_MS_MIN, n));
}
export const SHOT_EVENTS = new Set([
    'robotLaserHit', 'rearLaserHit', 'laserReflected',
    'bigGunHit', 'miniHowitzerHit',
    'beamPushed', 'beamPulled', 'scrambled', 'radioControlled', 'transverseHit',
    'flameThrowerFired',
    'mineExploded', 'seismicExploded', 'scramblerBombExploded', 'empFired',
    'missileExploded', 'payloadBurst',
]);
/** Movement events carry a destination; those are the steps worth
 * showing. Destruction is a step too, so a robot is seen to die rather
 * than simply being absent. */
export const DESTRUCTION = new Set([
    'destroyedPit', 'destroyedOffBoard', 'destroyedByDamage', 'crusherDestroyed',
    // A trap door opening under a robot that stayed put. Without it here
    // the death was not a step at all, so the robot was simply absent at
    // the end of the register instead of being seen to die ON the pit,
    // which is what an ordinary pit does.
    'trapDoorDestroyed',
]);
/**
 * Events that add damage, and how much.
 *
 * Where an event carries a count it is used -- a double laser does two --
 * and otherwise a point is assumed, which is what every other source
 * deals. An event not listed here changes nothing on the token.
 */
export const DAMAGE_EVENTS = new Set([
    'boardLaserHit', 'robotLaserHit', 'rearLaserHit', 'flamerDamage', 'flamerBurn',
    'flamingOilBurn', 'rampLandingDamage', 'bigGunHit', 'missileHit',
    'mineDamage', 'debrisDamage', 'seismicDamage',
    'flamingOilDamage', 'radioactiveWasteDamage', 'radiationDamage',
    'spikeDamage', 'spikeFloorDamage', 'speedBumpDamage', 'fallDamage',
    // Both fire and both hurt, and neither was here: a Mini-Howitzer round
    // and a laser a Reflector threw back down its own beam left the token
    // alone until the server's own damage arrived at the end of the
    // register.
    'miniHowitzerHit', 'laserReflected', 'flameThrowerBurn',
    'rammingGearHit', 'spikedWheelsHit', 'optionDamage',
]);
/**
 * A missile's own steps. These move a TOKEN, not a robot, and they carry
 * the owner's id so the log can file them -- so every caller that reads
 * `robotId` as "the thing that moved" has to check this set first, or a
 * missile drags its firer across the board with it.
 */
export const MISSILE_STEPS = new Set([
    'missileFlew', 'missileExploded', 'missileLost',
]);
/**
 * A flag riding a belt. These move the FLAG layer and nothing else, and
 * carry no robot id at all, so every caller that reads `robotId` as "the
 * thing that moved" has to check this set first.
 *
 * A step of its own so the flag moves when the belts run rather than the
 * instant the register resolves, which is when the view carrying its new
 * square arrives.
 */
export const FLAG_STEPS = new Set(['flagConveyed']);
/**
 * A device or a liquid put down, or goo struggled against or broken. A
 * step of its own so the token changes when it happens, rather than when
 * the next card plays. Carries no move, so every caller that reads `to`
 * or `robotId` as a robot's must take these out first, as it does a
 * missile's.
 */
export const TOKEN_STEPS = new Set(['tokenDropped', 'stuckInGoo', 'brokeFreeOfGoo']);
/**
 * A robot taking an archive: step E, the end of a register.
 *
 * The marker must not appear on the flag or the repair site before then.
 * The view carries where every marker ended up, so drawing from it put the
 * marker on the square at the top of the register -- before the robot had
 * been shown arriving, and sometimes before the flag it belongs to had
 * been carried there.
 *
 * These carry no square of their own: an archive is taken where the robot
 * is standing, and at this point in the playback that is exactly where the
 * board has it.
 *
 * `flagTouched` is not one of them. Every non-final flag is a repair site
 * as well, so scoring one logs `archivedAtFlag` in the same breath, and
 * taking both would spend a beat on nothing.
 */
export const ARCHIVE_STEPS = new Set(['archivedAtFlag', 'repairSiteTouched']);
/**
 * An Option gained, from a wrench or a chop shop, or swapped for another
 * or reloaded at a chop shop. A step so the table is told on the beat it
 * happens. Changes nothing on the board, and carries no square.
 */
export const OPTION_STEPS = new Set(['optionGranted', 'optionExchanged', 'ammoReloaded', 'reengineered']);
/**
 * Each hit as it landed: less whatever a shield stopped.
 *
 * A hit's event carries the whole shot, and `damageShielded` -- logged
 * after it, for the same robot -- says how much of it never landed. The
 * token went up by the whole shot and came back down when the register's
 * real damage arrived. The shielded points are taken off the hit they
 * follow, so the step count is the same either way.
 */
function netOfShields(events) {
    const out = events.slice();
    out.forEach((e, i) => {
        if (e.type !== 'damageShielded')
            return;
        for (let j = i - 1; j >= 0; j--) {
            const hit = out[j];
            if (hit.robotId !== e.robotId || !DAMAGE_EVENTS.has(hit.type))
                continue;
            out[j] = { ...hit, count: Math.max(0, (hit.count ?? 1) - (e.count ?? 1)) };
            return;
        }
    });
    return out;
}
/** Events that change what the board shows: a move, a hit, or a missile
 * crossing a square. */
export function playableSteps(events) {
    const steps = netOfShields(events).filter((e) => ((e.to !== undefined
        && (e.type === 'moved' || e.type === 'pushed'
            || e.type === 'portaled' || e.type === 'rotated'
            || e.type === 'teleported' || e.type === 'repulsed'
            || e.type === 'launched'))
        || DESTRUCTION.has(e.type)
        || DAMAGE_EVENTS.has(e.type)
        || MISSILE_STEPS.has(e.type)
        || FLAG_STEPS.has(e.type)
        || TOKEN_STEPS.has(e.type)
        || ARCHIVE_STEPS.has(e.type)
        || OPTION_STEPS.has(e.type)
        // A shot that neither moves its target nor damages it: a Pressor or
        // Tractor Beam, a Scrambler, a Radio Control. Nothing else in this
        // list catches one, so the beam had no beat to be drawn on and the
        // ray weapons crossed the board invisibly.
        || SHOT_EVENTS.has(e.type)));
    // A card's struggles in one patch of goo are one beat, at the count
    // they leave it on.
    return steps.filter((e, i) => !(e.type === 'stuckInGoo'
        && steps[i + 1]?.type === 'stuckInGoo' && steps[i + 1].tokenId === e.tokenId));
}
/** The squares a straight flight passes through, `from` excluded and
 * `to` included. Empty when nothing moved. */
function squaresAfter(from, to) {
    const dx = Math.sign(to.x - from.x);
    const dy = Math.sign(to.y - from.y);
    // A missile only ever flies along its facing, so a diagonal means the
    // two ends do not describe a flight this can walk. Jump it rather than
    // loop forever.
    if (dx !== 0 && dy !== 0)
        return [to];
    const distance = Math.max(Math.abs(to.x - from.x), Math.abs(to.y - from.y));
    const path = [];
    for (let i = 1; i <= distance; i++) {
        path.push({ x: from.x + dx * i, y: from.y + dy * i });
    }
    return path;
}
/**
 * Splits a missile's flight into one step per square.
 *
 * The engine resolves a whole phase of flight at once and records only
 * the ends of it, the same way it records a whole register of movement at
 * once. Robots are already animated a square at a time, so a missile that
 * crossed two squares in a single beat looked wrong beside them -- and
 * the square it passes THROUGH is worth seeing, since a missile goes off
 * on the first thing it meets rather than the square it was aimed at.
 *
 * The last square keeps the event's own type, so an explosion still goes
 * off exactly where it did and does not cost an extra beat.
 */
export function expandMissileFlights(steps) {
    const out = [];
    for (const step of steps) {
        if (!MISSILE_STEPS.has(step.type) || !step.from || !step.to) {
            out.push(step);
            continue;
        }
        const path = squaresAfter(step.from, step.to);
        // Went off without moving -- a wall on the very first square.
        if (path.length === 0) {
            out.push(step);
            continue;
        }
        let at = step.from;
        path.forEach((square, i) => {
            const last = i === path.length - 1;
            out.push({
                ...step,
                type: last ? step.type : 'missileFlew',
                from: at,
                to: square,
                // The blast is the explosion's, on the square it went off in.
                squares: last ? step.squares : undefined,
            });
            at = square;
        });
    }
    return out;
}
/**
 * Whether a logged advance belongs to `turn`'s replay: its registers, its
 * end of turn, and the next turn's Deal, where a scrambler bomb goes
 * off. All of it happens before anyone programs
 * again.
 */
export function inTurnReplay(entry, turn) {
    if (entry.turn === turn)
        return entry.register !== null || entry.phase === 'awaitingEndOfTurn';
    return entry.turn === turn + 1 && entry.phase === 'deal';
}
/** How long a shot stays on the board, in beats. */
export const SHOT_BEATS = 1.5;
/** How long a step stays on the board before the next one replaces it. */
export function beatMs(step, stepMs) {
    return SHOT_EVENTS.has(step.type) ? stepMs * SHOT_BEATS : stepMs;
}
/**
 * How long a turn's replay takes, in milliseconds: `entries` are its
 * registers' logged advances that carry a frame, in order. Each plays its
 * steps for `beatMs` apiece and holds one more beat after -- two after the
 * last -- and one with no step holds a single beat.
 */
export function turnReplayMs(entries, stepMs = PLAYBACK_MS) {
    let ms = 0;
    entries.forEach((entry, i) => {
        const steps = expandMissileFlights(playableSteps(entry.events));
        const last = i === entries.length - 1;
        ms += steps.length === 0
            ? stepMs
            : steps.reduce((sum, step) => sum + beatMs(step, stepMs), 0) + (last ? 2 : 1) * stepMs;
    });
    return ms;
}
